import React, { ReactNode, useState } from 'react'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { t, Trans, Plural } from '@lingui/macro'

import { useSearchLazyQuery } from 'front-end/src/pages/search-results-page/operations/searchResults.codegen'

import { getBackgroundColor } from 'shared/colorUtils'
import { SearchSortEnum } from 'shared/enums/search'
import { navigateTo } from '../../app/util/appHistory'
import { linkBuilder, staticLinks } from 'shared/linkBuilder'
import { Dropdown } from '../common/dropdown/Dropdown'
import Input from '../common/form/input/Input'
import IconCalendar from '../common/icon/IconCalendar'
import IconDoc from '../common/icon/IconDoc'
import IconLayers from '../common/icon/IconLayers'
import IconMap from '../common/icon/IconMap'
import Loader from '../common/loader/Loader'
import { notification } from '../common/notifications/Notifications'
import IconBadge from '../common/icon/IconBadge'
import { useMainHeaderOrgQuery } from './MainHeader'
import { Box, LinkOverlay, ButtonIcon } from 'front-end/src/components'
import IconClose from '../common/icon/IconClose'

import IconMagnifier from 'front-end/src/components/common/icon/IconMagnifier'

import './HeaderSearch.less'

interface AutocompleteResultItem {
  title: string | null | undefined
  name?: string | null | undefined
  link: string
  icon: ReactNode | null
}

export const HeaderSearch = () => {
  const [query, setQuery] = useState('')
  const [viewSearch, setViewSearch] = useState(false)

  const [autocompleteResults, { data }] = useSearchLazyQuery()
  const location = useLocation()
  const match = useRouteMatch<{ query: string }>('/search/:query')
  const organisation = useMainHeaderOrgQuery()
  if (!organisation) {
    return null
  }

  const updateAutocomplete = (queryString) => {
    if (queryString.length > 1) {
      autocompleteResults({
        variables: {
          query: queryString.substring(0, 255),
          sort: SearchSortEnum.MOST_RELEVANT,
          limit: 20
        }
      })
    }
  }

  const renderAutocompleteResults = () => {
    if (!query || match?.params.query === query) {
      return null
    }
    const minValue = 2
    if (query.length < minValue) {
      return (
        <Box className="DropdownLabel">
          <Trans>Minimum {minValue} characters</Trans>
        </Box>
      )
    }

    if (!data) {
      return <Loader />
    }

    if (data && data.search.length === 0) {
      return (
        <Box className="DropdownLabel">
          <Trans>
            No result found for <strong>{query.substring(0, 255)}</strong>
          </Trans>
        </Box>
      )
    }
    const results = data.search
      .map((result) => {
        switch (result.__typename) {
          case 'TopicModelAsMember':
            return {
              title: result.currentTopicVersion?.title,
              link: linkBuilder.topicUrl(result.id),
              icon: <IconDoc />
            }
          case 'WorkspaceModelAsMember':
            return {
              title: result.name,
              link: linkBuilder.workspaceUrl(result.id),
              icon: <IconLayers />
            }
          case 'PathwayModelAsStudent':
            return {
              title: result.title,
              link: linkBuilder.pathwayDetail(result.id),
              icon: <IconMap />
            }
          case 'EventModelAsStudent':
            return {
              title: result.eventTitle,
              link: linkBuilder.eventDetail(result.id),
              icon: <IconCalendar />
            }
          case 'CertificationModelAsStudent':
            return {
              title: result.name,
              link: linkBuilder.certificationsDetail(result.id),
              icon: <IconBadge />
            }
          default:
            console.error(
              `support for resource type ${result.__typename} is not implemented`
            )
        }
      })
      .filter((r) => r) as AutocompleteResultItem[]

    return (
      <>
        <Box className="DropdownLabel">
          <Plural
            value={results.length}
            one="# result found"
            other="# results found"
          />
        </Box>
        {results.map((result) => (
          <LinkOverlay
            className="DropdownLink"
            key={result.link}
            to={result.link}
          >
            {result.icon} {result.title}
          </LinkOverlay>
        ))}
      </>
    )
  }

  return (
    <Box className="MainHeader__search" ml={[0, '8px', '32px']}>
      <Box
        className="MainHeader__search__toggle"
        onClick={() => setViewSearch(true)}
      >
        <span className="icon icon-magnifier" />
      </Box>
      <form
        className={`MainHeader__search__form ${viewSearch ? 'show' : ''}`}
        onSubmit={(e) => {
          if (!query) {
            notification.warning(t`Please fill search query`)
            return null
          }
          e.preventDefault()
          navigateTo(linkBuilder.searchUrl(query))
        }}
        style={{ backgroundColor: getBackgroundColor(organisation) }}
      >
        <Dropdown
          positionOfContent="left"
          tabIndex={undefined}
          content={renderAutocompleteResults()}
        >
          <Box className="Dropdown__handler">
            <Input
              className="form-textInput"
              name="search"
              title="search"
              tabIndex={0}
              autoComplete="off"
              placeholder={t`Search for answers`}
              debouncedOnChange={(value) => updateAutocomplete(value)}
              onChange={(value) => {
                setQuery(value)
              }}
              value={query}
            />

            <span className="MainHeader__search__form__submit">
              <IconMagnifier />
            </span>

            <ButtonIcon
              tabIndex={query.length && 0}
              visibility={query.length ? 'visible' : 'hidden'}
              className="delete"
              aria-label={t`Delete`}
              onClick={() => {
                setQuery('')
                setViewSearch(false)
                if (location.pathname.startsWith('/search')) {
                  navigateTo(staticLinks.home)
                }
              }}
            >
              <IconClose />
            </ButtonIcon>
          </Box>
        </Dropdown>
      </form>
    </Box>
  )
}
